// @ts-nocheck
/**
 * Teacher Content Management Component
 * Complete content creation and distribution interface for educators
 * Implements Carbon Design System components with drag-drop file upload,
 * assignment creation, grading workflow, and resource sharing
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Column,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  FileUploader,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tile,
  Tag,
  InlineNotification,
  Loading,
  DatePicker,
  DatePickerInput,
  NumberInput,
  ComboBox,
  ProgressIndicator,
  ProgressStep,
  OverflowMenu,
  OverflowMenuItem,
  MultiSelect,
  Pagination,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Link,
  Card,
  CardBody,
  CardHeader,
  IconButton,
} from '@carbon/react';
import {
  Upload,
  DocumentAdd,
  Share,
  Edit,
  View,
  Download,
  Delete,
  Email,
  UserMultiple,
  Assignment,
  FolderOpen,
  Analytics,
  CheckmarkOutline,
  WarningAlt,
  Information,
  Chat,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// TypeScript Interfaces
interface ContentItem {
  content_id: string;
  title: string;
  description: string;
  content_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  subject: string;
  status: string;
  created_at: string;
  updated_at: string;
  assignment_count: number;
}

interface Assignment {
  assignment_id: string;
  title: string;
  description: string;
  subject: string;
  due_date: string;
  total_points: number;
  status: string;
  created_at: string;
  total_submissions: number;
  graded_submissions: number;
  content_count: number;
}

interface Communication {
  communication_id: string;
  subject: string;
  message: string;
  type: string;
  recipient_count: number;
  include_parents: boolean;
  created_at: string;
}

interface SharedResource {
  share_id: string;
  sharing_teacher_id: string;
  sharing_teacher_email: string;
  share_type: string;
  share_note: string;
  content_count: number;
  created_at: string;
  access_level: string;
  share_status: string;
}

export const TeacherContentManagement: React.FC = () => {
  // Authentication
  const { user, token } = useAuthStore();

  // State Management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    subtitle?: string;
  } | null>(null);

  // Content Library State
  const [contentLibrary, setContentLibrary] = useState<ContentItem[]>([]);
  const [contentFilters, setContentFilters] = useState({
    type: '',
    subject: '',
    search: '',
  });

  // Assignment State
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [assignmentFilters, setAssignmentFilters] = useState({
    status: '',
    subject: '',
  });

  // Communication State
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [sharedResources, setSharedResources] = useState<SharedResource[]>([]);

  // Modal States
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [communicationModalOpen, setCommunicationModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Form States
  const [uploadForm, setUploadForm] = useState({
    file: null as File | null,
    title: '',
    description: '',
    contentType: 'course_material',
    subject: '',
  });

  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    subject: '',
    dueDate: '',
    points: 100,
    contentIds: [] as string[],
    instructions: '',
  });

  const [communicationForm, setCommunicationForm] = useState({
    recipients: [] as string[],
    subject: '',
    message: '',
    type: 'announcement',
    includeParents: false,
  });

  // API Configuration
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };

  const axiosFileConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // API Functions
  const loadContentLibrary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/content/library`, {
        ...axiosConfig,
        params: {
          type: contentFilters.type || undefined,
          subject: contentFilters.subject || undefined,
          limit: 50,
        },
      });

      if (response.data.success) {
        setContentLibrary(response.data.data.content);
      }
    } catch (error) {
      console.error('Error loading content library:', error);
      setNotification({
        type: 'error',
        title: 'Failed to load content library',
        subtitle: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  }, [contentFilters, axiosConfig]);

  const loadAssignments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/assignments`, {
        ...axiosConfig,
        params: {
          status: assignmentFilters.status || undefined,
          limit: 25,
        },
      });

      if (response.data.success) {
        setAssignments(response.data.data.assignments);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
      setNotification({
        type: 'error',
        title: 'Failed to load assignments',
        subtitle: 'Please try again later',
      });
    }
  }, [assignmentFilters, axiosConfig]);

  const loadSharedResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/resources/shared-with-me`, {
        ...axiosConfig,
        params: { limit: 20 },
      });

      if (response.data.success) {
        setSharedResources(response.data.data.sharedResources);
      }
    } catch (error) {
      console.error('Error loading shared resources:', error);
    }
  }, [axiosConfig]);

  // Upload Content Handler
  const handleUploadContent = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      setNotification({
        type: 'warning',
        title: 'Missing required fields',
        subtitle: 'Please provide a file and title',
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('contentType', uploadForm.contentType);
      formData.append('subject', uploadForm.subject);

      const response = await axios.post(`${API_BASE_URL}/v1/teacher/content/upload`, formData, axiosFileConfig);

      if (response.data.success) {
        setNotification({
          type: 'success',
          title: 'Content uploaded successfully',
          subtitle: response.data.message,
        });
        setUploadModalOpen(false);
        setUploadForm({
          file: null,
          title: '',
          description: '',
          contentType: 'course_material',
          subject: '',
        });
        loadContentLibrary();
      }
    } catch (error) {
      console.error('Error uploading content:', error);
      setNotification({
        type: 'error',
        title: 'Upload failed',
        subtitle: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  // Create Assignment Handler
  const handleCreateAssignment = async () => {
    if (!assignmentForm.title || !assignmentForm.dueDate) {
      setNotification({
        type: 'warning',
        title: 'Missing required fields',
        subtitle: 'Please provide title and due date',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/v1/teacher/assignments/create`, assignmentForm, axiosConfig);

      if (response.data.success) {
        setNotification({
          type: 'success',
          title: 'Assignment created successfully',
          subtitle: response.data.message,
        });
        setAssignmentModalOpen(false);
        setAssignmentForm({
          title: '',
          description: '',
          subject: '',
          dueDate: '',
          points: 100,
          contentIds: [],
          instructions: '',
        });
        loadAssignments();
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      setNotification({
        type: 'error',
        title: 'Assignment creation failed',
        subtitle: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  // Send Communication Handler
  const handleSendCommunication = async () => {
    if (!communicationForm.subject || !communicationForm.message || !communicationForm.recipients.length) {
      setNotification({
        type: 'warning',
        title: 'Missing required fields',
        subtitle: 'Please provide subject, message, and recipients',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/v1/teacher/communications/send`,
        communicationForm,
        axiosConfig
      );

      if (response.data.success) {
        setNotification({
          type: 'success',
          title: 'Communication sent successfully',
          subtitle: response.data.message,
        });
        setCommunicationModalOpen(false);
        setCommunicationForm({
          recipients: [],
          subject: '',
          message: '',
          type: 'announcement',
          includeParents: false,
        });
      }
    } catch (error) {
      console.error('Error sending communication:', error);
      setNotification({
        type: 'error',
        title: 'Communication failed',
        subtitle: 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  // Effect Hooks
  useEffect(() => {
    if (user && token) {
      loadContentLibrary();
      loadAssignments();
      loadSharedResources();
    }
  }, [user, token, loadContentLibrary, loadAssignments, loadSharedResources]);

  // Content Type Options
  const contentTypeOptions = [
    { id: 'course_material', text: 'Course Material' },
    { id: 'assignment', text: 'Assignment' },
    { id: 'resource', text: 'Resource' },
    { id: 'presentation', text: 'Presentation' },
    { id: 'video', text: 'Video' },
    { id: 'document', text: 'Document' },
  ];

  // Subject Options
  const subjectOptions = [
    { id: 'mathematics', text: 'Mathematics' },
    { id: 'science', text: 'Science' },
    { id: 'english', text: 'English' },
    { id: 'history', text: 'History' },
    { id: 'art', text: 'Art' },
    { id: 'music', text: 'Music' },
    { id: 'physical_education', text: 'Physical Education' },
  ];

  // Communication Type Options
  const communicationTypeOptions = [
    { id: 'announcement', text: 'Announcement' },
    { id: 'alert', text: 'Alert' },
    { id: 'reminder', text: 'Reminder' },
    { id: 'grade_notification', text: 'Grade Notification' },
  ];

  return (
    <Grid className="teacher-content-management">
      <Column lg={16} md={8} sm={4}>
        {/* Page Header */}
        <div className="page-header" style={{ marginBottom: '2rem' }}>
          <h1>Content Management</h1>
          <p>Create, organize, and distribute educational content with streamlined workflows</p>
        </div>

        {/* Notification */}
        {notification && (
          <InlineNotification
            kind={notification.type}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            style={{ marginBottom: '1rem' }}
          />
        )}

        {/* Main Tabs */}
        <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex)}>
          <TabList aria-label="Content management tabs">
            <Tab>Content Library</Tab>
            <Tab>Assignments</Tab>
            <Tab>Communications</Tab>
            <Tab>Resource Sharing</Tab>
          </TabList>

          <TabPanels>
            {/* Content Library Tab */}
            <TabPanel>
              <Grid style={{ marginTop: '1rem' }}>
                <Column lg={12} md={6} sm={4}>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <Button kind="primary" renderIcon={Upload} onClick={() => setUploadModalOpen(true)}>
                      Upload Content
                    </Button>
                    <Select
                      id="content-type-filter"
                      labelText="Filter by type"
                      value={contentFilters.type}
                      onChange={e => setContentFilters(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <SelectItem value="" text="All types" />
                      {contentTypeOptions.map(option => (
                        <SelectItem key={option.id} value={option.id} text={option.text} />
                      ))}
                    </Select>
                    <Select
                      id="content-subject-filter"
                      labelText="Filter by subject"
                      value={contentFilters.subject}
                      onChange={e => setContentFilters(prev => ({ ...prev, subject: e.target.value }))}
                    >
                      <SelectItem value="" text="All subjects" />
                      {subjectOptions.map(option => (
                        <SelectItem key={option.id} value={option.id} text={option.text} />
                      ))}
                    </Select>
                  </div>

                  {loading ? (
                    <Loading description="Loading content library..." />
                  ) : (
                    <DataTable
                      rows={contentLibrary.map((item, index) => ({
                        id: item.content_id,
                        ...item,
                      }))}
                      headers={[
                        { key: 'title', header: 'Title' },
                        { key: 'content_type', header: 'Type' },
                        { key: 'subject', header: 'Subject' },
                        { key: 'file_size', header: 'Size' },
                        { key: 'assignment_count', header: 'Used in Assignments' },
                        { key: 'created_at', header: 'Created' },
                        { key: 'actions', header: 'Actions' },
                      ]}
                    >
                      {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
                        <TableContainer title="Content Library">
                          <Table {...getTableProps()}>
                            <TableHead>
                              <TableRow>
                                {headers.map(header => (
                                  <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.map(row => (
                                <TableRow {...getRowProps({ row })}>
                                  {row.cells.map(cell => {
                                    if (cell.info.header === 'file_size') {
                                      return <TableCell key={cell.id}>{Math.round(cell.value / 1024)} KB</TableCell>;
                                    }
                                    if (cell.info.header === 'content_type') {
                                      return (
                                        <TableCell key={cell.id}>
                                          <Tag type="outline">
                                            {contentTypeOptions.find(opt => opt.id === cell.value)?.text || cell.value}
                                          </Tag>
                                        </TableCell>
                                      );
                                    }
                                    if (cell.info.header === 'created_at') {
                                      return (
                                        <TableCell key={cell.id}>{new Date(cell.value).toLocaleDateString()}</TableCell>
                                      );
                                    }
                                    if (cell.info.header === 'actions') {
                                      return (
                                        <TableCell key={cell.id}>
                                          <OverflowMenu ariaLabel="Actions">
                                            <OverflowMenuItem itemText="View" />
                                            <OverflowMenuItem itemText="Edit" />
                                            <OverflowMenuItem itemText="Download" />
                                            <OverflowMenuItem itemText="Share" />
                                            <OverflowMenuItem itemText="Delete" isDelete />
                                          </OverflowMenu>
                                        </TableCell>
                                      );
                                    }
                                    return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </DataTable>
                  )}
                </Column>
              </Grid>
            </TabPanel>

            {/* Assignments Tab */}
            <TabPanel>
              <Grid style={{ marginTop: '1rem' }}>
                <Column lg={12} md={6} sm={4}>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <Button kind="primary" renderIcon={DocumentAdd} onClick={() => setAssignmentModalOpen(true)}>
                      Create Assignment
                    </Button>
                    <Select
                      id="assignment-status-filter"
                      labelText="Filter by status"
                      value={assignmentFilters.status}
                      onChange={e => setAssignmentFilters(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <SelectItem value="" text="All statuses" />
                      <SelectItem value="active" text="Active" />
                      <SelectItem value="closed" text="Closed" />
                      <SelectItem value="archived" text="Archived" />
                    </Select>
                  </div>

                  <DataTable
                    rows={assignments.map((assignment, index) => ({
                      id: assignment.assignment_id,
                      ...assignment,
                    }))}
                    headers={[
                      { key: 'title', header: 'Title' },
                      { key: 'subject', header: 'Subject' },
                      { key: 'due_date', header: 'Due Date' },
                      { key: 'total_points', header: 'Points' },
                      { key: 'total_submissions', header: 'Submissions' },
                      { key: 'graded_submissions', header: 'Graded' },
                      { key: 'status', header: 'Status' },
                      { key: 'actions', header: 'Actions' },
                    ]}
                  >
                    {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
                      <TableContainer title="Assignments">
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map(header => (
                                <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map(row => (
                              <TableRow {...getRowProps({ row })}>
                                {row.cells.map(cell => {
                                  if (cell.info.header === 'due_date') {
                                    return (
                                      <TableCell key={cell.id}>{new Date(cell.value).toLocaleDateString()}</TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'status') {
                                    return (
                                      <TableCell key={cell.id}>
                                        <Tag
                                          type={
                                            cell.value === 'active' ? 'green' : cell.value === 'closed' ? 'red' : 'gray'
                                          }
                                        >
                                          {cell.value}
                                        </Tag>
                                      </TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'actions') {
                                    return (
                                      <TableCell key={cell.id}>
                                        <OverflowMenu ariaLabel="Actions">
                                          <OverflowMenuItem itemText="View Submissions" />
                                          <OverflowMenuItem itemText="Edit" />
                                          <OverflowMenuItem itemText="Duplicate" />
                                          <OverflowMenuItem itemText="Close" />
                                          <OverflowMenuItem itemText="Archive" />
                                        </OverflowMenu>
                                      </TableCell>
                                    );
                                  }
                                  return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>
                </Column>
              </Grid>
            </TabPanel>

            {/* Communications Tab */}
            <TabPanel>
              <Grid style={{ marginTop: '1rem' }}>
                <Column lg={12} md={6} sm={4}>
                  <Button
                    kind="primary"
                    renderIcon={Email}
                    onClick={() => setCommunicationModalOpen(true)}
                    style={{ marginBottom: '1rem' }}
                  >
                    Send Communication
                  </Button>

                  <Tile>
                    <h4>Recent Communications</h4>
                    <p>Communication history and parent notification tracking will be displayed here.</p>
                  </Tile>
                </Column>
              </Grid>
            </TabPanel>

            {/* Resource Sharing Tab */}
            <TabPanel>
              <Grid style={{ marginTop: '1rem' }}>
                <Column lg={6} md={4} sm={4}>
                  <Tile>
                    <h4>Share Resources</h4>
                    <p>Collaborate with other teachers by sharing your content library.</p>
                    <Button
                      kind="secondary"
                      renderIcon={Share}
                      onClick={() => setShareModalOpen(true)}
                      style={{ marginTop: '1rem' }}
                    >
                      Share Content
                    </Button>
                  </Tile>
                </Column>
                <Column lg={6} md={4} sm={4}>
                  <Tile>
                    <h4>Shared with Me</h4>
                    <p>{sharedResources.length} resources shared by other teachers</p>
                    {sharedResources.length > 0 && (
                      <StructuredListWrapper>
                        <StructuredListHead>
                          <StructuredListRow>
                            <StructuredListCell head>Teacher</StructuredListCell>
                            <StructuredListCell head>Content</StructuredListCell>
                            <StructuredListCell head>Shared</StructuredListCell>
                          </StructuredListRow>
                        </StructuredListHead>
                        <StructuredListBody>
                          {sharedResources.slice(0, 5).map(resource => (
                            <StructuredListRow key={resource.share_id}>
                              <StructuredListCell>{resource.sharing_teacher_email}</StructuredListCell>
                              <StructuredListCell>{resource.content_count} items</StructuredListCell>
                              <StructuredListCell>
                                {new Date(resource.created_at).toLocaleDateString()}
                              </StructuredListCell>
                            </StructuredListRow>
                          ))}
                        </StructuredListBody>
                      </StructuredListWrapper>
                    )}
                  </Tile>
                </Column>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Upload Content Modal */}
        <Modal
          open={uploadModalOpen}
          onRequestClose={() => setUploadModalOpen(false)}
          modalHeading="Upload Content"
          primaryButtonText="Upload"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleUploadContent}
          size="md"
        >
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <FileUploader
                labelTitle="Select file"
                labelDescription="Drag and drop files here or click to browse"
                buttonLabel="Choose file"
                filenameStatus="edit"
                accept={['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.jpg', '.png', '.mp4']}
                multiple={false}
                iconDescription="Delete file"
                onChange={event => {
                  const files = event.target.files;
                  if (files && files.length > 0) {
                    setUploadForm(prev => ({ ...prev, file: files[0] }));
                  }
                }}
              />

              <TextInput
                id="upload-title"
                labelText="Title"
                placeholder="Enter content title"
                value={uploadForm.title}
                onChange={e => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                style={{ marginTop: '1rem' }}
              />

              <TextArea
                id="upload-description"
                labelText="Description"
                placeholder="Enter content description"
                value={uploadForm.description}
                onChange={e => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                style={{ marginTop: '1rem' }}
              />

              <Select
                id="upload-content-type"
                labelText="Content type"
                value={uploadForm.contentType}
                onChange={e => setUploadForm(prev => ({ ...prev, contentType: e.target.value }))}
                style={{ marginTop: '1rem' }}
              >
                {contentTypeOptions.map(option => (
                  <SelectItem key={option.id} value={option.id} text={option.text} />
                ))}
              </Select>

              <Select
                id="upload-subject"
                labelText="Subject"
                value={uploadForm.subject}
                onChange={e => setUploadForm(prev => ({ ...prev, subject: e.target.value }))}
                style={{ marginTop: '1rem' }}
              >
                <SelectItem value="" text="Select subject" />
                {subjectOptions.map(option => (
                  <SelectItem key={option.id} value={option.id} text={option.text} />
                ))}
              </Select>
            </Column>
          </Grid>
        </Modal>

        {/* Create Assignment Modal */}
        <Modal
          open={assignmentModalOpen}
          onRequestClose={() => setAssignmentModalOpen(false)}
          modalHeading="Create Assignment"
          primaryButtonText="Create"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleCreateAssignment}
          size="lg"
        >
          <Grid>
            <Column lg={8} md={4} sm={4}>
              <TextInput
                id="assignment-title"
                labelText="Assignment Title"
                placeholder="Enter assignment title"
                value={assignmentForm.title}
                onChange={e => setAssignmentForm(prev => ({ ...prev, title: e.target.value }))}
              />

              <TextArea
                id="assignment-description"
                labelText="Description"
                placeholder="Enter assignment description"
                value={assignmentForm.description}
                onChange={e => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))}
                style={{ marginTop: '1rem' }}
              />

              <Select
                id="assignment-subject"
                labelText="Subject"
                value={assignmentForm.subject}
                onChange={e => setAssignmentForm(prev => ({ ...prev, subject: e.target.value }))}
                style={{ marginTop: '1rem' }}
              >
                <SelectItem value="" text="Select subject" />
                {subjectOptions.map(option => (
                  <SelectItem key={option.id} value={option.id} text={option.text} />
                ))}
              </Select>
            </Column>
            <Column lg={8} md={4} sm={4}>
              <DatePicker datePickerType="single">
                <DatePickerInput
                  id="assignment-due-date"
                  labelText="Due Date"
                  placeholder="mm/dd/yyyy"
                  value={assignmentForm.dueDate}
                  onChange={e => setAssignmentForm(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </DatePicker>

              <NumberInput
                id="assignment-points"
                label="Total Points"
                value={assignmentForm.points}
                onChange={(e, { value }) => setAssignmentForm(prev => ({ ...prev, points: value || 100 }))}
                min={0}
                style={{ marginTop: '1rem' }}
              />

              <TextArea
                id="assignment-instructions"
                labelText="Instructions"
                placeholder="Enter detailed instructions for students"
                value={assignmentForm.instructions}
                onChange={e => setAssignmentForm(prev => ({ ...prev, instructions: e.target.value }))}
                style={{ marginTop: '1rem' }}
              />
            </Column>
          </Grid>
        </Modal>

        {/* Send Communication Modal */}
        <Modal
          open={communicationModalOpen}
          onRequestClose={() => setCommunicationModalOpen(false)}
          modalHeading="Send Communication"
          primaryButtonText="Send"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleSendCommunication}
          size="md"
        >
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <TextInput
                id="communication-subject"
                labelText="Subject"
                placeholder="Enter communication subject"
                value={communicationForm.subject}
                onChange={e => setCommunicationForm(prev => ({ ...prev, subject: e.target.value }))}
              />

              <TextArea
                id="communication-message"
                labelText="Message"
                placeholder="Enter your message"
                value={communicationForm.message}
                onChange={e => setCommunicationForm(prev => ({ ...prev, message: e.target.value }))}
                style={{ marginTop: '1rem' }}
              />

              <Select
                id="communication-type"
                labelText="Communication Type"
                value={communicationForm.type}
                onChange={e => setCommunicationForm(prev => ({ ...prev, type: e.target.value }))}
                style={{ marginTop: '1rem' }}
              >
                {communicationTypeOptions.map(option => (
                  <SelectItem key={option.id} value={option.id} text={option.text} />
                ))}
              </Select>
            </Column>
          </Grid>
        </Modal>
      </Column>
    </Grid>
  );
};

export default TeacherContentManagement;
